import {
  CheckCircleFilled,
  CommentOutlined,
  DownOutlined,
  FileTextOutlined,
  MailOutlined,
  MessageOutlined,
  ReloadOutlined,
  SendOutlined
} from "@ant-design/icons"
import {
  App,
  Button,
  Dropdown,
  Flex,
  Tag,
  Tooltip,
  Typography,
  theme
} from "antd"
import type { MenuProps } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { RichTextEditor } from "../../../../components/RichTextEditor/RichTextEditor.tsx"
import { isRichTextEmpty } from "../../../../components/RichTextEditor/rich-text-html.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { unwrapEnvelope } from "../../../../utils/api/unwrap-envelope.ts"
import { pickFormError } from "../../../../utils/handlers/pick-form-error.ts"
import {
  getEmailTemplates,
  renderEmailTemplateForLead,
  sendEmailToLead,
  uploadEmailTemplateImage
} from "../../../EmailTemplates/api/services.ts"
import { htmlToPlainText } from "../../../EmailTemplates/helpers/html-to-plain-text.ts"
import { type EmailTemplate } from "../../../EmailTemplates/types/email-template.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import {
  EmailRecipientsInput,
  isEmailAddress
} from "./EmailRecipientsInput.tsx"
import { OverviewCardTitle } from "./OverviewCardIcon.tsx"

const { Text } = Typography

interface LeadOverviewEmailTemplatesCardProps {
  leadId?: string
  /** The customer's address, the one the email goes to; empty when they have none. */
  recipient?: string
  /** Sending writes to the lead's customer, so it rides the lead-edit right. */
  canSend?: boolean
}

type TemplatesState = "loading" | "failed" | "loaded"

function ChannelChoice() {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  return (
    <div
      role={"radiogroup"}
      aria-label={t("leadWorkspace.emailCard.channel")}
      className={"grid gap-3 sm:grid-cols-2"}
    >
      <div
        role={"radio"}
        aria-checked
        tabIndex={0}
        className={"flex items-center justify-between rounded-lg px-4 py-3"}
        style={{
          border: `2px solid ${token.colorPrimary}`,
          background: token.colorBgContainer
        }}
      >
        <Flex align={"center"} gap={8}>
          <MailOutlined aria-hidden style={{ color: token.colorPrimary }} />
          <Text>{t("leadWorkspace.emailCard.channelEmail")}</Text>
        </Flex>
        <CheckCircleFilled
          aria-hidden
          style={{ color: token.colorPrimary, fontSize: 18 }}
        />
      </div>
      <div
        role={"radio"}
        aria-checked={false}
        aria-disabled
        className={
          "flex cursor-not-allowed items-center justify-between rounded-lg px-4 py-3"
        }
        style={{
          border: `1px solid ${token.colorBorderSecondary}`,
          background: token.colorFillQuaternary
        }}
      >
        <Flex align={"center"} gap={8}>
          <MessageOutlined aria-hidden className={"text-gray-400"} />
          <Text type={"secondary"}>
            {t("leadWorkspace.emailCard.channelSms")}
          </Text>
        </Flex>
        <Tag bordered={false} className={"!m-0 rounded-full"}>
          {t("leadWorkspace.emailCard.comingSoon")}
        </Tag>
      </div>
    </div>
  )
}

/**
 * The lead's email to its customer: always open to write in, with a Settings
 * template one click away. A picked template is filled in with this lead's
 * details and stays editable; what is sent is what the composer shows.
 */
function LeadOverviewEmailTemplatesCard({
  leadId,
  recipient = "",
  canSend = false
}: LeadOverviewEmailTemplatesCardProps) {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const canCreate = useAppSelector(
    (state) =>
      !!state.auth.permissions?.codes?.settings?.email_templates
        ?.settings_email_template_create
  )

  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [templatesState, setTemplatesState] =
    useState<TemplatesState>("loading")
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [templateId, setTemplateId] = useState<string | undefined>(undefined)
  const [moreTo, setMoreTo] = useState<string[]>([])
  const [cc, setCc] = useState<string[]>([])
  const [showCc, setShowCc] = useState(false)
  const [filling, setFilling] = useState(false)
  const [sending, setSending] = useState(false)
  const fillRequest = useRef(0)

  const loadTemplates = async () => {
    setTemplatesState("loading")
    // The backend's default page size (10) is smaller than the 6 seeded
    // defaults plus a team's own, so an unbounded call would truncate the list.
    const response = await getEmailTemplates({ page_size: 1000 })
    if (!response.ok) {
      setTemplatesState("failed")
      return
    }
    setTemplates(response.data?.results ?? [])
    setTemplatesState("loaded")
  }

  useEffect(() => {
    void loadTemplates()
  }, [])

  const fillFrom = async (template: EmailTemplate) => {
    const requestId = ++fillRequest.current
    setTemplateId(template.id)
    if (!leadId) {
      setSubject(template.subject)
      setBody(template.body)
      return
    }
    setFilling(true)
    const response = await renderEmailTemplateForLead(template.id, leadId)
    if (requestId !== fillRequest.current) return
    setFilling(false)
    if (response.ok && response.data) {
      setSubject(response.data.subject)
      setBody(response.data.body)
      return
    }
    setSubject(template.subject)
    setBody(template.body)
    void message.warning(t("leadWorkspace.emailCard.fillFailed"))
  }

  const pickTemplate = (template: EmailTemplate) => {
    if (!subject.trim() && isRichTextEmpty(body)) {
      void fillFrom(template)
      return
    }
    modal.confirm({
      centered: true,
      title: t("leadWorkspace.emailCard.replaceTitle", { name: template.name }),
      content: t("leadWorkspace.emailCard.replaceBody"),
      okText: t("leadWorkspace.emailCard.replaceOk"),
      cancelText: t("leadWorkspace.emailCard.cancel"),
      onOk: () => void fillFrom(template)
    })
  }

  const templateMenu: MenuProps["items"] =
    templatesState === "failed"
      ? [
          {
            key: "retry",
            icon: <ReloadOutlined />,
            label: t("leadWorkspace.emailCard.loadFailedRetry"),
            onClick: () => void loadTemplates()
          }
        ]
      : templates.length === 0
        ? [
            {
              key: "none",
              disabled: true,
              label: t("leadWorkspace.emailCard.noTemplates")
            },
            ...(canCreate
              ? [
                  {
                    key: "create",
                    label: (
                      <Link to={RoutesPaths.settings.emailTemplates}>
                        {t("leadWorkspace.emailCard.createTemplate")}
                      </Link>
                    )
                  }
                ]
              : [])
          ]
        : templates.map((template) => ({
            key: template.id,
            label: template.name,
            onClick: () => pickTemplate(template)
          }))

  const handleImageUpload = async (file: File): Promise<{ url: string }> => {
    const response = await uploadEmailTemplateImage(file)
    const data = response.ok
      ? unwrapEnvelope<{ url: string }>(response.data)
      : undefined
    if (!data?.url) {
      void message.error(t("leadWorkspace.emailCard.imageFailed"))
      throw new Error("Image upload failed")
    }
    return data
  }

  const toLine = recipient ? [recipient, ...moreTo] : moreTo

  // Someone added to To leaves Cc, so no one is shown, or sent, on both lines.
  const changeTo = (next: string[]) => {
    const onTo = new Set(
      [recipient, ...next].map((address) => address.toLowerCase())
    )
    setMoreTo(next)
    setCc((current) =>
      current.filter((address) => !onTo.has(address.toLowerCase()))
    )
  }
  const notAddresses = [...moreTo, ...cc].filter(
    (address) => !isEmailAddress(address)
  )
  const notAnAddress = (addresses: string[]) => {
    const wrong = addresses.filter((address) => !isEmailAddress(address))
    return wrong.length ? (
      <Text type={"danger"} role={"alert"}>
        {t("leadWorkspace.emailCard.notAnAddress", {
          addresses: wrong.join(", ")
        })}
      </Text>
    ) : null
  }

  const sendDisabledReason = !canSend
    ? t("leadWorkspace.emailCard.noPermission")
    : !recipient
      ? t("leadWorkspace.emailCard.noAddress")
      : notAddresses.length
        ? t("leadWorkspace.emailCard.notAnAddress", {
            addresses: notAddresses.join(", ")
          })
        : !subject.trim() || isRichTextEmpty(body)
          ? t("leadWorkspace.emailCard.needsContent")
          : undefined

  const confirmSend = () => {
    if (!leadId || sendDisabledReason) return
    modal.confirm({
      centered: true,
      title: t("leadWorkspace.emailCard.confirmTitle"),
      content: (
        <Flex vertical gap={4}>
          <Text>
            <Text type={"secondary"}>{t("leadWorkspace.emailCard.to")}</Text>
            {toLine.join(", ")}
          </Text>
          {cc.length > 0 && (
            <Text>
              <Text type={"secondary"}>
                {t("leadWorkspace.emailCard.ccPrefix")}
              </Text>
              {cc.join(", ")}
            </Text>
          )}
          <Text>
            <Text type={"secondary"}>
              {t("leadWorkspace.emailCard.subjectLabel")}
            </Text>
            {subject}
          </Text>
        </Flex>
      ),
      okText: t("leadWorkspace.emailCard.send"),
      cancelText: t("leadWorkspace.emailCard.cancel"),
      onOk: async () => {
        setSending(true)
        const response = await sendEmailToLead({
          lead: leadId,
          subject,
          body,
          ...(templateId ? { template: templateId } : {}),
          ...(moreTo.length ? { to: moreTo } : {}),
          ...(cc.length ? { cc } : {})
        })
        setSending(false)
        if (response.ok) {
          void message.success(t("leadWorkspace.emailCard.sent", { recipient }))
          setSubject("")
          setBody("")
          setTemplateId(undefined)
          setMoreTo([])
          setCc([])
          setShowCc(false)
          return
        }
        const refusal = pickFormError(response.data)
        void message.error(
          (typeof refusal === "string" ? refusal : refusal?.lead?.[0]) ??
            t("leadWorkspace.emailCard.sendFailed")
        )
      }
    })
  }

  // The body is HTML, so a plain `writeText` would paste literal `<p>` markup.
  // Where the browser supports it, both an HTML and a plain-text form go in one
  // clipboard item, so a rich compose window keeps the formatting.
  const copyEmail = async () => {
    try {
      if (navigator.clipboard.write && typeof ClipboardItem !== "undefined") {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([body], { type: "text/html" }),
            "text/plain": new Blob([htmlToPlainText(body)], {
              type: "text/plain"
            })
          })
        ])
      } else {
        await navigator.clipboard.writeText(htmlToPlainText(body))
      }
      void message.success(t("leadWorkspace.emailCard.copied"))
    } catch {
      void message.error(t("leadWorkspace.emailCard.copyFailed"))
    }
  }

  return (
    <WorkspaceCard
      cardId={"email-templates"}
      title={
        <OverviewCardTitle icon={<CommentOutlined />} accent={"teal"}>
          {t("leadWorkspace.emailCard.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("leadWorkspace.emailCard.title")}
      className={"h-full"}
    >
      <Flex vertical gap={"middle"}>
        <ChannelChoice />

        <Flex vertical gap={6}>
          <Flex justify={"space-between"} align={"center"} gap={"small"}>
            <Text type={"secondary"}>
              <label htmlFor={"lead-email-to"}>
                {t("leadWorkspace.emailCard.toLabel")}
              </label>
            </Text>
            {!showCc && (
              <Button
                type={"link"}
                size={"small"}
                onClick={() => setShowCc(true)}
              >
                {t("leadWorkspace.emailCard.addCc")}
              </Button>
            )}
          </Flex>
          <EmailRecipientsInput
            id={"lead-email-to"}
            ariaLabel={t("leadWorkspace.emailCard.toLabel")}
            fixed={recipient || undefined}
            fixedHint={t("leadWorkspace.emailCard.customerAddress")}
            value={moreTo}
            onChange={changeTo}
            placeholder={t("leadWorkspace.emailCard.toPlaceholder")}
          />
          {notAnAddress(moreTo)}
        </Flex>

        {showCc && (
          <Flex vertical gap={6}>
            <Text type={"secondary"}>
              <label htmlFor={"lead-email-cc"}>
                {t("leadWorkspace.emailCard.ccLabel")}
              </label>
            </Text>
            <EmailRecipientsInput
              id={"lead-email-cc"}
              ariaLabel={t("leadWorkspace.emailCard.ccLabel")}
              value={cc}
              onChange={setCc}
              besides={toLine}
              placeholder={t("leadWorkspace.emailCard.ccPlaceholder")}
            />
            {notAnAddress(cc)}
          </Flex>
        )}

        <Flex vertical gap={6}>
          <Flex justify={"space-between"} align={"center"} gap={"small"}>
            <Text type={"secondary"}>
              <label htmlFor={"lead-email-subject"}>
                {t("leadWorkspace.emailCard.subject")}
              </label>
            </Text>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: templateMenu,
                className: "max-h-72 overflow-y-auto"
              }}
              disabled={templatesState === "loading"}
            >
              <Button icon={<FileTextOutlined />} loading={filling}>
                {t("leadWorkspace.emailCard.useTemplate")}{" "}
                <DownOutlined className={"text-xs"} />
              </Button>
            </Dropdown>
          </Flex>
          <TextInput
            id={"lead-email-subject"}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder={t("leadWorkspace.emailCard.subject")}
          />
        </Flex>

        <Flex vertical gap={6}>
          <Text type={"secondary"}>{t("leadWorkspace.emailCard.body")}</Text>
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder={t("leadWorkspace.emailCard.bodyPlaceholder")}
            minHeight={180}
            ariaLabel={t("leadWorkspace.emailCard.body")}
            onImageUpload={canSend ? handleImageUpload : undefined}
          />
        </Flex>

        <Flex gap={"small"} wrap>
          <Button
            onClick={() => void copyEmail()}
            disabled={isRichTextEmpty(body)}
          >
            {t("leadWorkspace.emailCard.copyEmail")}
          </Button>
          {leadId && (
            <Tooltip title={sendDisabledReason}>
              <Button
                type={"primary"}
                icon={<SendOutlined />}
                loading={sending}
                disabled={!!sendDisabledReason}
                onClick={confirmSend}
              >
                {t("leadWorkspace.emailCard.send")}
              </Button>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    </WorkspaceCard>
  )
}

export { LeadOverviewEmailTemplatesCard }
